/* ═══════════════════════════════════════════════════════
   2819 STORE — Mission Homepage JS
   Custom cursor · Nav · Scroll reveals · Count-up
   Video embeds · Dark mode · Page transitions
   ═══════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── Light/Dark Mode ────────────────────────────── */
  /* v10: LIGHT is the default register (white ground). The switch
     in the nav is a CSS-driven sliding toggle keyed off
     [data-theme]; we do NOT rewrite its markup here. Preference
     persists via localStorage. Anti-flash is handled inline in
     <head> of theme.liquid. */
  (function initTheme() {
    const root = document.documentElement;
    const toggle = document.querySelector('[data-theme-toggle]');
    let theme = localStorage.getItem('2819-theme') || 'light';
    root.setAttribute('data-theme', theme);
    if (toggle) {
      toggle.setAttribute('aria-checked', theme === 'dark' ? 'true' : 'false');
      toggle.addEventListener('click', function () {
        theme = theme === 'dark' ? 'light' : 'dark';
        root.setAttribute('data-theme', theme);
        localStorage.setItem('2819-theme', theme);
        toggle.setAttribute('aria-checked', theme === 'dark' ? 'true' : 'false');
      });
    }
  })();

  /* ── Custom Cursor ──────────────────────────────── */
  (function initCursor() {
    const dot = document.getElementById('cursorDot');
    const ring = document.getElementById('cursorRing');
    if (!dot || !ring) return;
    if (window.matchMedia('(hover: none)').matches) {
      dot.style.display = 'none'; ring.style.display = 'none'; return;
    }

    let mx = 0, my = 0, rx = 0, ry = 0;
    let cursorLabel = null;

    document.addEventListener('mousemove', function (e) {
      mx = e.clientX; my = e.clientY;
      dot.style.left = mx + 'px'; dot.style.top = my + 'px';
    });

    // Ring lerp
    function lerp(a, b, t) { return a + (b - a) * t; }
    function animRing() {
      rx = lerp(rx, mx, 0.14); ry = lerp(ry, my, 0.14);
      ring.style.left = rx + 'px'; ring.style.top = ry + 'px';
      requestAnimationFrame(animRing);
    }
    animRing();

    // Cursor expand/label on hover targets
    document.addEventListener('mouseover', function (e) {
      const target = e.target.closest('[data-cursor]');
      if (target) {
        ring.classList.add('label-mode');
        ring.dataset.label = target.dataset.cursor;
      }
      const interactive = e.target.closest('a, button, [role="button"]');
      if (interactive && !target) {
        ring.classList.add('expanded');
      }
    });
    document.addEventListener('mouseout', function (e) {
      const target = e.target.closest('[data-cursor]');
      if (target) {
        ring.classList.remove('label-mode');
        delete ring.dataset.label;
      }
      const interactive = e.target.closest('a, button, [role="button"]');
      if (interactive && !target) {
        ring.classList.remove('expanded');
      }
    });
  })();

  /* ── Scroll Reveal ──────────────────────────────── */
  (function initScrollReveal() {
    const reveals = document.querySelectorAll('.reveal, .reveal-stack, .stagger-words');
    if (!reveals.length) return;
    if (!('IntersectionObserver' in window)) {
      reveals.forEach(function (el) { el.classList.add('revealed'); });
      return;
    }
    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    reveals.forEach(function (el) { observer.observe(el); });
  })();

  /* ── v8: Parallax Image In-View (scroll-coupled scale-down) ──── */
  (function initParallaxImg() {
    const imgs = document.querySelectorAll('.parallax-img, .parallax-img--lg');
    if (!imgs.length || !('IntersectionObserver' in window)) return;
    const obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -80px 0px' });
    imgs.forEach(function (el) { obs.observe(el); });
  })();

  /* ── Hero Clip Reveal ───────────────────────────── */
  /* Fires on load — hero is above fold so IntersectionObserver
     would trigger immediately with no drama. Instead we fire
     on DOMContentLoaded with a single rAF to guarantee paint. */
  (function initHeroReveal() {
    const hero = document.getElementById('hero');
    if (!hero) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        hero.classList.add('clip-ready');
      });
    });
  })();

  /* ── v11: Hero Parallax (bg word drifts on scroll) ──── */
  (function initHeroParallax() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const hero = document.getElementById('hero');
    if (!hero) return;
    const bg = hero.querySelector('.hero__bg-word');
    if (!bg) return;
    let ticking = false;
    function update() {
      const r = hero.getBoundingClientRect();
      const p = Math.min(Math.max(-r.top / window.innerHeight, 0), 1.3);
      bg.style.transform = 'translateY(' + (p * 110) + 'px)';
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  })();

  /* ── v11: 3D Product-Card Tilt (image only — leaves reveal intact) ── */
  (function initCardTilt() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(hover: none)').matches) return;
    document.querySelectorAll('.product-card').forEach(function (card) {
      const img = card.querySelector('.product-card-image');
      if (!img) return;
      card.style.perspective = '900px';
      card.addEventListener('mousemove', function (e) {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        img.style.transition = 'transform .08s linear';
        img.style.transform = 'rotateY(' + (px * 6) + 'deg) rotateX(' + (-py * 6) + 'deg)';
      });
      card.addEventListener('mouseleave', function () {
        img.style.transition = 'transform .35s cubic-bezier(.16,1,.3,1)';
        img.style.transform = '';
      });
    });
  })();

  /* ── Count-Up Animation ─────────────────────────── */
  (function initCountUp() {
    const counters = document.querySelectorAll('[data-count]');
    if (!counters.length || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseInt(el.dataset.count, 10);
        const duration = 1800;
        const start = performance.now();
        function frame(now) {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          // Ease out cubic
          const eased = 1 - Math.pow(1 - progress, 3);
          const value = Math.floor(eased * target);
          // Format with commas for large numbers — unless opted out
          // (data-no-format: brand numbers like 2819 must never render as "2,819")
          var noFmt = el.hasAttribute('data-no-format');
          el.textContent = (!noFmt && value >= 1000)
            ? value.toLocaleString()
            : value.toString();
          if (progress < 1) requestAnimationFrame(frame);
          else el.textContent = (!noFmt && target >= 1000) ? target.toLocaleString() : target.toString();
        }
        requestAnimationFrame(frame);
        observer.unobserve(el);
      });
    }, { threshold: 0.3 });

    counters.forEach(function (el) { observer.observe(el); });
  })();

  /* ── Mobile Drawer ──────────────────────────────── */
  (function initMobileDrawer() {
    const toggle = document.getElementById('navToggle');
    const drawer = document.getElementById('mobileDrawer');
    const overlay = document.getElementById('mobileOverlay');
    const closeBtn = document.getElementById('drawerClose');
    const closeLinks = document.querySelectorAll('[data-close-drawer]');
    if (!toggle || !drawer) return;

    let lastFocus = null;

    // Closed drawer must not be keyboard-reachable
    if (!drawer.classList.contains('open')) drawer.setAttribute('inert', '');

    function openDrawer() {
      lastFocus = document.activeElement;
      drawer.classList.add('open');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.removeAttribute('inert');
      if (overlay) overlay.classList.add('visible');
      if (toggle) { toggle.classList.add('open'); toggle.setAttribute('aria-expanded', 'true'); }
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    }
    function closeDrawer() {
      if (!drawer.classList.contains('open')) return;
      drawer.classList.remove('open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      if (overlay) overlay.classList.remove('visible');
      if (toggle) { toggle.classList.remove('open'); toggle.setAttribute('aria-expanded', 'false'); }
      document.body.style.overflow = '';
      const focusTarget = (lastFocus && document.contains(lastFocus)) ? lastFocus : toggle;
      if (focusTarget) focusTarget.focus();
    }

    toggle.addEventListener('click', function () {
      drawer.classList.contains('open') ? closeDrawer() : openDrawer();
    });
    closeBtn && closeBtn.addEventListener('click', closeDrawer);
    overlay.addEventListener('click', closeDrawer);
    closeLinks.forEach(function (link) { link.addEventListener('click', closeDrawer); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('open')) closeDrawer();
    });
  })();

  /* ── Sticky Nav scroll effect ───────────────────── */
  (function initStickyNav() {
    const nav = document.getElementById('mainNav');
    if (!nav) return;
    let scrolled = false;
    window.addEventListener('scroll', function () {
      const shouldScrolled = window.scrollY > 20;
      if (shouldScrolled !== scrolled) {
        scrolled = shouldScrolled;
        nav.style.borderBottomColor = scrolled ? 'var(--c-border)' : 'transparent';
        nav.classList.toggle('nav--solid', scrolled);
      }
    }, { passive: true });
  })();

  /* ── Video Embeds ───────────────────────────────── */
  (function initVideoEmbeds() {
    const embeds = document.querySelectorAll('.video-embed');
    embeds.forEach(function (embed) {
      const videoId = embed.dataset.videoId;
      const playBtn = embed.querySelector('.video-embed__play');
      if (!playBtn || !videoId || videoId.startsWith('YOUR_')) {
        // Placeholder mode: show the thumb with a "coming soon" state
        return;
      }
      playBtn.addEventListener('click', function () {
        const iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube.com/embed/' + videoId + '?autoplay=1&rel=0&modestbranding=1';
        iframe.allow = 'autoplay; encrypted-media; fullscreen';
        iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:none;';
        iframe.setAttribute('allowfullscreen', '');

        const thumb = embed.querySelector('.video-embed__thumb, .video-embed__thumb--featured');
        if (thumb) {
          thumb.style.position = 'relative';
          // Clear thumb content and replace with iframe
          while (thumb.firstChild) thumb.removeChild(thumb.firstChild);
          thumb.appendChild(iframe);
        }
      });
    });
  })();

  /* ── Page Transition Overlay ────────────────────── */
  (function initPageTransition() {
    const overlay = document.querySelector('.page-transition') || (() => {
      const el = document.createElement('div');
      el.className = 'page-transition';
      document.body.appendChild(el);
      return el;
    })();

    // Entrance: sweep out
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        overlay.classList.remove('entering');
      });
    });

    // Exit: sweep in on internal nav
    document.addEventListener('click', function (e) {
      const link = e.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href');
      // Only intercept internal page links
      if (!href) return;
      if (href.startsWith('#') || href.startsWith('http') || href.startsWith('mailto') || href.startsWith('tel')) return;
      // Never intercept checkout — conversion risk
      if (href.startsWith('/checkout') || href.includes('checkout')) return;
      // Never intercept cart operations
      if (href.includes('/cart/change') || href.includes('/cart/update') || href.includes('/cart/add')) return;
      // Never intercept _blank or account flows
      if (link.getAttribute('target') === '_blank') return;
      if (href.startsWith('/account')) return;
      if (href.startsWith('javascript')) return;
      // Never steal modified clicks (open-in-new-tab), non-left buttons,
      // downloads, or clicks something else already handled
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.button !== 0 || e.defaultPrevented) return;
      if (link.hasAttribute('download')) return;
      // Drawer triggers + cart URL: the drawer owns these, don't double-navigate
      if (link.hasAttribute('data-cart-open') || href === '/cart' || href.startsWith('/cart?')) return;
      e.preventDefault();
      overlay.classList.add('entering');
      // Reduced motion: navigate immediately, no 480ms black hold
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        window.location.href = href;
        return;
      }
      setTimeout(function () {
        window.location.href = href;
      }, 480);
    });

    // On pageshow, ensure overlay is out
    window.addEventListener('pageshow', function () {
      overlay.classList.remove('entering');
    });
  })();

  /* ── Cursor label rendering ─────────────────────── */
  (function initCursorLabels() {
    const ring = document.getElementById('cursorRing');
    if (!ring) return;
    // Inject a style for the pseudo-label
    const style = document.createElement('style');
    style.textContent = `
      #cursorRing.label-mode::after {
        content: attr(data-label);
        position: absolute; top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
        font-size: 8px; font-weight: 700;
        letter-spacing: 0.12em; text-transform: uppercase;
        color: white; white-space: nowrap; pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  })();

  /* ── v13: Cart Drawer (Shopify AJAX + Section Rendering API) ── */
  (function initCartDrawer() {
    var drawer = document.getElementById('cartDrawer');
    if (!drawer) return;
    var overlay = document.getElementById('cartDrawerOverlay');
    var lastFocus = null;

    // Closed drawer must not be keyboard-reachable (inert + visibility in section CSS)
    if (!drawer.classList.contains('open')) drawer.setAttribute('inert', '');

    function openCart() {
      lastFocus = document.activeElement;
      drawer.classList.add('open');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.removeAttribute('inert');
      if (overlay) overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('cart-drawer-open'); // hides sticky CTA (covers Checkout on mobile)
      var closeBtn = drawer.querySelector('[data-cart-close]');
      if (closeBtn) closeBtn.focus();
    }
    function closeCart() {
      if (!drawer.classList.contains('open')) return;
      drawer.classList.remove('open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      if (overlay) overlay.classList.remove('open');
      document.body.style.overflow = '';
      document.body.classList.remove('cart-drawer-open');
      if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
    }

    document.addEventListener('click', function (e) {
      var openEl = e.target.closest('[data-cart-open]');
      if (openEl) { e.preventDefault(); openCart(); return; }
      var closeEl = e.target.closest('[data-cart-close]');
      if (closeEl) { e.preventDefault(); closeCart(); return; }
      var chg = e.target.closest('[data-cart-change]');
      if (chg) { e.preventDefault(); changeLine(chg.getAttribute('data-cart-change'), chg.getAttribute('data-qty')); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('open')) closeCart();
    });

    // Refresh drawer contents from the Section Rendering API.
    // Source is our own first-party Shopify section HTML; nodes are moved in
    // via DOMParser + replaceChildren (no innerHTML sink).
    function moveInto(parsedDoc, id) {
      var src = parsedDoc.getElementById(id);
      var dest = document.getElementById(id);
      if (src && dest) dest.replaceChildren.apply(dest, Array.prototype.slice.call(src.childNodes));
    }
    function refresh(openAfter) {
      // NOTE: no 'Accept: application/json' header and no product pathname here.
      // On /products/* URLs that header makes Shopify return the product JSON
      // and IGNORE ?sections= (verified on this store), so the drawer silently
      // never refreshed after add-to-cart on PDPs. Root path + default headers
      // returns the Section Rendering payload everywhere.
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(root + '?sections=cart-drawer')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var html = data['cart-drawer'];
          if (!html) return;
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          moveInto(parsed, 'cartDrawerItems');
          moveInto(parsed, 'cartDrawerFoot');
          var nc = parsed.querySelector('[data-cart-count]');
          if (nc) {
            var isEmpty = (parseInt(nc.textContent, 10) || 0) === 0;
            document.querySelectorAll('[data-cart-count], .nav__cart-count').forEach(function (c) {
              c.textContent = nc.textContent;
              c.classList.toggle('is-empty', isEmpty);
            });
          }
          if (openAfter) openCart();
        })
        .catch(function () { window.location.href = '/cart'; });
    }

    function changeLine(line, qty) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity: parseInt(qty, 10) })
      }).then(function (r) {
        if (!r.ok) throw new Error('change rejected');
        refresh(false);
      }).catch(function () { window.location.href = '/cart'; });
    }

    // Surface Shopify's error (sold out / exceeds stock) next to the ATC button
    function showAtcError(form, message) {
      var el = form.querySelector('.atc-error');
      if (!el) {
        el = document.createElement('p');
        el.className = 'atc-error mono';
        el.setAttribute('role', 'alert');
        el.style.cssText = 'color:var(--c-red);font-size:11px;letter-spacing:.08em;margin:10px 0 0;';
        var btn = form.querySelector('[type="submit"]');
        if (btn && btn.parentNode) btn.parentNode.insertBefore(el, btn.nextSibling);
        else form.appendChild(el);
      }
      el.textContent = message;
    }

    // Intercept add-to-cart → open the drawer instead of leaving the page.
    // A 422 here (sold out, qty exceeds stock) is the NORMAL case on drop night —
    // it must show the reason, never a false "ADDED".
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      if (btn) btn.setAttribute('disabled', '');
      var prevErr = form.querySelector('.atc-error');
      if (prevErr) prevErr.textContent = '';
      // Use the items-array payload: Shopify validates inventory upfront for this
      // format and returns 422 (sold out / exceeds stock). The legacy single-item
      // form payload returns 200 and puts phantom stock in the cart (verified on
      // this store), which would defeat the error handling below.
      var fd = new FormData(form);
      var item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity'), 10) || 1 };
      var props = {};
      fd.forEach(function (v, k) {
        var m = k.match(/^properties\[(.+)\]$/);
        if (m) props[m[1]] = v;
      });
      if (Object.keys(props).length) item.properties = props;
      if (fd.get('selling_plan')) item.selling_plan = parseInt(fd.get('selling_plan'), 10);
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [item] }) })
        .then(function (r) {
          return r.json().then(function (data) { return { ok: r.ok, data: data }; });
        })
        .then(function (res) {
          if (!res.ok) {
            var msg = (res.data && (res.data.description || res.data.message)) || 'Could not add to bag. Try again.';
            showAtcError(form, msg);
            return;
          }
          refresh(true);
        })
        .catch(function () { form.submit(); })
        .finally(function () { if (btn) btn.removeAttribute('disabled'); });
    });
  })();

  /* ── Colorway Swatches ──────────────────────────────
     Colorways are separate products, so the card shows one style and the
     rest as swatch links (snippets/style-group.liquid). Hover or focus a
     swatch and the card image becomes that colorway; a click just follows
     the link, so this is an enhancement and never a dependency.

     Delegated, so cards rendered later still work. The swapped image is
     decoded BEFORE it goes on screen — a half-loaded swap would flash an
     empty box — and the box is a fixed 3/4, so nothing can shift.
     The cross-fade itself is CSS, which is where prefers-reduced-motion
     turns it off. */
  (function initSwatches() {
    var cache = {};

    function sized(url, img) {
      var want = (img.clientWidth || 300) * (window.devicePixelRatio || 1);
      var w = want > 400 ? 600 : (want > 300 ? 400 : 300);
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
    }

    function show(card, swatch) {
      var img = card.querySelector('[data-card-img]');
      var src = swatch.getAttribute('data-swatch-img');
      if (!img || !src) return;
      if (!img.hasAttribute('data-swatch-original')) {
        img.setAttribute('data-swatch-original', img.getAttribute('src') || '');
        img.setAttribute('data-swatch-srcset', img.getAttribute('srcset') || '');
        img.setAttribute('data-swatch-alt', img.getAttribute('alt') || '');
      }
      var url = sized(src, img);
      card.setAttribute('data-swatch-want', url);
      var apply = function () {
        // The pointer may have moved on while this was decoding.
        if (card.getAttribute('data-swatch-want') !== url) return;
        img.setAttribute('srcset', '');
        img.src = url;
        img.alt = swatch.getAttribute('aria-label') || img.alt;
        card.classList.add('product-card--swatched');
      };
      if (cache[url]) { apply(); return; }
      var pre = new Image();
      pre.onload = function () { cache[url] = true; apply(); };
      pre.onerror = function () { restore(card); };  // dead url: leave the real image up
      pre.src = url;
    }

    function restore(card) {
      var img = card.querySelector('[data-card-img]');
      card.removeAttribute('data-swatch-want');
      if (!img || !img.hasAttribute('data-swatch-original')) return;
      img.setAttribute('srcset', img.getAttribute('data-swatch-srcset') || '');
      img.src = img.getAttribute('data-swatch-original');
      img.alt = img.getAttribute('data-swatch-alt') || '';
      card.classList.remove('product-card--swatched');
    }

    function bind(type, handler) {
      document.addEventListener(type, function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        var swatch = t.closest('.swatch');
        if (!swatch) return;
        var card = swatch.closest('.product-card');
        if (card) handler(card, swatch);
      }, true);
    }

    bind('mouseover', function (card, swatch) { show(card, swatch); });
    bind('focusin', function (card, swatch) { show(card, swatch); });
    bind('mouseout', function (card) { restore(card); });
    bind('focusout', function (card) { restore(card); });
  })();

})();
